import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { taskApi } from '../services/api'
import toast from 'react-hot-toast'

const TaskContext = createContext()

export function TaskProvider({ children }) {
  const [tasks, setTasks] = useState([])
  const [todayTasks, setTodayTasks] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(false)
  const [categories, setCategories] = useState([])

  // Fetch all tasks
  const fetchTasks = useCallback(async (filters = {}) => {
    setLoading(true)
    try {
      const data = await taskApi.getAll(filters)
      setTasks(data.tasks)
      return data.tasks
    } catch (error) {
      toast.error('Failed to load tasks')
      console.error(error)
      return []
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch today's tasks
  const fetchTodayTasks = useCallback(async () => {
    try {
      const data = await taskApi.getToday()
      setTodayTasks(data.tasks)
      return data.tasks
    } catch (error) {
      console.error('Failed to load today\'s tasks:', error)
      return []
    }
  }, [])

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await taskApi.getStats()
      setStats(data)
      return data
    } catch (error) {
      console.error('Failed to load stats:', error)
      return null
    }
  }, [])

  // Fetch categories
  const fetchCategories = useCallback(async () => {
    try {
      const data = await taskApi.getCategories()
      setCategories(data.categories)
      return data.categories
    } catch (error) {
      console.error('Failed to load categories:', error)
      return []
    }
  }, [])

  // Create task
  const createTask = async (taskData) => {
    try {
      const data = await taskApi.create(taskData)
      setTasks(prev => [data.task, ...prev])
      await fetchTodayTasks()
      await fetchStats()
      await fetchCategories()
      toast.success('Task created successfully')
      return data.task
    } catch (error) {
      toast.error('Failed to create task')
      throw error
    }
  }

  // Update task
  const updateTask = async (id, updates) => {
    try {
      const data = await taskApi.update(id, updates)
      setTasks(prev => prev.map(t => t.id === id ? data.task : t))
      await fetchTodayTasks()
      await fetchStats()
      toast.success('Task updated')
      return data.task
    } catch (error) {
      toast.error('Failed to update task')
      throw error
    }
  }

  // Delete task
  const deleteTask = async (id) => {
    try {
      await taskApi.delete(id)
      setTasks(prev => prev.filter(t => t.id !== id))
      await fetchTodayTasks()
      await fetchStats()
      await fetchCategories()
      toast.success('Task deleted')
    } catch (error) {
      toast.error('Failed to delete task')
      throw error
    }
  }

  // Toggle task completion
  const toggleTaskComplete = async (id) => {
    try {
      const data = await taskApi.toggleComplete(id)
      setTasks(prev => prev.map(t => t.id === id ? data.task : t))
      await fetchTodayTasks()
      await fetchStats()
      
      if (data.task.status === 'completed') {
        toast.success('Task completed! 🎉')
      }
      
      return data.task
    } catch (error) {
      toast.error('Failed to update task')
      throw error
    }
  }

  // Parse natural language
  const parseNaturalLanguage = async (text) => {
    try {
      const data = await taskApi.parseNaturalLanguage(text)
      return data.parsed
    } catch (error) {
      console.error('Failed to parse text:', error)
      return null
    }
  }

  // Initial load
  useEffect(() => {
    fetchTasks()
    fetchTodayTasks()
    fetchStats()
    fetchCategories()
  }, [fetchTasks, fetchTodayTasks, fetchStats, fetchCategories])

  // Refresh data periodically
  useEffect(() => {
    const interval = setInterval(() => {
      fetchTodayTasks()
      fetchStats()
    }, 60000) // Refresh every minute

    return () => clearInterval(interval)
  }, [fetchTodayTasks, fetchStats])

  const value = {
    tasks,
    todayTasks,
    stats,
    loading,
    categories,
    fetchTasks,
    fetchTodayTasks,
    fetchStats,
    createTask,
    updateTask,
    deleteTask,
    toggleTaskComplete,
    parseNaturalLanguage,
  }

  return (
    <TaskContext.Provider value={value}>
      {children}
    </TaskContext.Provider>
  )
}

export function useTasks() {
  const context = useContext(TaskContext)
  if (!context) {
    throw new Error('useTasks must be used within a TaskProvider')
  }
  return context
}